import { useState } from "react";
import { Eye, EyeOff, X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useFetch } from "../../hooks/useFetch";

type ModalResetPasswordProps = {
	open: boolean;
	onClose: () => void;
	/** Token extraído de la URL (?token=...) */
	token: string;
	/** Email extraído de la URL (?email=...) */
	email: string;
};

/*Solicitar recuperación de contraseña con un email válido (comprobar que se genera reset_token y reset_token_exp en la DB).*/


export function ModalResetPassword({
	open,
	onClose,
	token,
	email,
}: ModalResetPasswordProps) {
	const { execute } = useFetch<
		{ success: boolean; message?: string },
		{
			email: string;
			token: string;
			newPassword: string;
			confirmNewPassword: string;
		}
	>({
		endpoint: "/verify/reset-password",
		method: "POST",
		auto: false,
	});

	const [newPassword, setNewPassword] = useState("");
	const [confirmNewPassword, setconfirmNewPassword] = useState("");
	const [showNew, setShowNew] = useState(false);
	const [showConfirm, setShowConfirm] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [success, setSuccess] = useState(false);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const navigate = useNavigate();

	if (!open) return null;

	const isTokenMissing = !token || !email;

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		setError(null);
		if (!newPassword) {
			setError("Ingresa la nueva contraseña");
			return;
		}
		if (newPassword.length < 6) {
			setError("La contraseña debe tener al menos 6 caracteres");
			return;
		}
		if (newPassword !== confirmNewPassword) {
			setError("Las contraseñas no coinciden");
			return;
		}

		setIsSubmitting(true);
		try {
			await execute({
				endpoint: "/verify/reset-password",
				query: { email },
				method: "POST",
				body: { email, token, newPassword, confirmNewPassword },
			});
			setSuccess(true);
		} catch (err) {
			const rawMessage =
				err instanceof Error
					? err.message
					: "No se pudo actualizar la contraseña. Intenta de nuevo.";
			if (/token|expir|vencid|inv[aá]lid/i.test(rawMessage)) {
				setError(
					"El enlace de recuperación es inválido o ha vencido. Solicita uno nuevo.",
				);
			} else {
				setError(rawMessage);
			}
		} finally {
			setIsSubmitting(false);
		}
	};
	/** Token extraído de la URL (?token=...) */
	const handleClose = () => {
		setNewPassword("");
		setconfirmNewPassword("");
		setShowNew(false);
		setShowConfirm(false);
		setError(null);
		setSuccess(false);
		setIsSubmitting(false);
		onClose();
		navigate("/login");
	};

	return (
		<div className='fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4'>
			<div className='relative w-full max-w-sm rounded-2xl bg-white px-8 py-8 shadow-[0_16px_40px_rgba(15,23,42,0.12)]'>
				<button
					type='button'
					onClick={handleClose}
					aria-label='Cerrar'
					className='absolute right-4 top-4 cursor-pointer rounded-full p-1 text-slate-400 transition hover:bg-slate-100 hover:text-slate-600'
				>
					<X className='h-4 w-4' />
				</button>

				{isTokenMissing ? (
					<div className='text-center'>
						<h2 className='text-xl font-medium text-slate-700'>
							Enlace inválido
						</h2>
						<p className='mt-3 text-sm text-slate-500'>
							El enlace de restablecimiento no es válido. Por favor solicita uno nuevo desde la pantalla de inicio de sesión.
						</p>
						<button
							type='button'
							onClick={handleClose}
							className='mt-6 w-full cursor-pointer rounded-full bg-primary py-2.5 text-sm font-semibold text-white transition hover:bg-primary-hover'
						>
							Ir al inicio de sesión
						</button>
					</div>
				) : success ? (
					<div className='text-center'>
						<h2 className='text-xl font-medium text-slate-700'>
							Contraseña Actualizada
						</h2>
						<p className='mt-3 text-sm text-slate-500'>
							Tu contraseña ha sido actualizada exitosamente. Ya puedes iniciar sesión con tu nueva contraseña.
						</p>
						<button
							type='button'
							onClick={handleClose}
							className='mt-6 w-full cursor-pointer rounded-full bg-primary py-2.5 text-sm font-semibold text-white transition hover:bg-primary-hover'
						>
							Iniciar Sesión
						</button>
					</div>
				) : (
					<>
						<h2 className='text-center text-xl font-medium text-slate-700'>
							Nueva Contraseña
						</h2>
						<p className='mt-3 text-center text-sm text-slate-500'>
							Ingresa tu nueva contraseña para{" "}
							<span className='font-medium text-slate-700'>{email}</span>
						</p>

						<form onSubmit={handleSubmit} className='mt-6 space-y-4'>
							{error ? (
								<div className='rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700'>
									{error}
								</div>
							) : null}

							<div className='space-y-2'>
								<label className='block text-sm font-medium text-slate-600'>
									Contraseña Nueva
								</label>
								<div className='relative'>
									<input
										type={showNew ? "text" : "password"}
										value={newPassword}
										onChange={(e) => setNewPassword(e.target.value)}
										placeholder='••••••••'
										className='w-full rounded-xl border border-slate-300 bg-white px-4 py-3 pr-10 text-sm text-slate-700 outline-none transition focus:border-primary focus:ring-2 focus:ring-secondary'
										autoComplete='new-password'
										required
									/>
									<button
										type='button'
										aria-label={showNew ? "Ocultar contraseña" : "Mostrar contraseña"}
										onClick={() => setShowNew((v) => !v)}
										className='absolute right-3 top-1/2 -translate-y-1/2 cursor-pointer rounded-md p-1 text-slate-400 transition hover:text-slate-600'
									>
										{showNew ? <EyeOff className='h-4 w-4' /> : <Eye className='h-4 w-4' />}
									</button>
								</div>
							</div>

							<div className='space-y-2'>
								<label className='block text-sm font-medium text-slate-600'>
									Confirmar Contraseña
								</label>
								<div className='relative'>
									<input
										type={showConfirm ? "text" : "password"}
										value={confirmNewPassword}
										onChange={(e) => setconfirmNewPassword(e.target.value)}
										placeholder='••••••••'
										className='w-full rounded-xl border border-slate-300 bg-white px-4 py-3 pr-10 text-sm text-slate-700 outline-none transition focus:border-primary focus:ring-2 focus:ring-secondary'
										autoComplete='new-password'
										required
									/>
									<button
										type='button'
										aria-label={showConfirm ? "Ocultar contraseña" : "Mostrar contraseña"}
										onClick={() => setShowConfirm((v) => !v)}
										className='absolute right-3 top-1/2 -translate-y-1/2 cursor-pointer rounded-md p-1 text-slate-400 transition hover:text-slate-600'
									>
										{showConfirm ? <EyeOff className='h-4 w-4' /> : <Eye className='h-4 w-4' />}
									</button>
								</div>
							</div>

							<button
								type='submit'
								disabled={isSubmitting}
								className='w-full cursor-pointer rounded-full bg-primary py-3 text-sm font-semibold text-white transition hover:bg-primary-hover disabled:cursor-not-allowed disabled:opacity-60'
							>
								{isSubmitting ? "Actualizando..." : "Actualizar Contraseña"}
							</button>
						</form>
					</>
				)}
			</div>
		</div>
	);
}
